import { useState } from "react";
import { DateTimeInput } from "../components/DateInput";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/session";
import { ago, dateTime } from "../lib/format";
import type { Task } from "../lib/types";
import { Empty, ErrorText, Field, Form, Loading, PageHeader, str } from "../components/ui";
import { registerWarmer } from "../lib/smooth";

const tasksQuery = (orgId: string, base: string, status: string, assigned: string) => ({
  queryKey: [orgId, "tasks", status, assigned],
  queryFn: () => api<{ tasks: Task[] }>(`${base}/tasks?status=${status}&assigned=${assigned}`),
});
registerWarmer("tasks", (qc, { orgId, base }) => { qc.prefetchQuery({ ...tasksQuery(orgId, base, "open", "me"), staleTime: 5_000 }); });
const fromLabel: Record<string, string> = { member: "Team", agent: "AI agent", automation: "Automation", system: "SVENEX" };

export default function Tasks() {
  const { base, orgId, allows } = useOrg();
  const qc = useQueryClient();
  const [status, setStatus] = useState("open");
  // Everyone starts with their own tasks; managers can look wider.
  const [assigned, setAssigned] = useState("me");
  const listQuery = tasksQuery(orgId, base, status, assigned);
  // Switching filters keeps the current list on screen (dimmed) until the new one arrives.
  const tasks = useQuery({ ...listQuery, placeholderData: (prev) => prev });
  // Ticking a task shows at once; the row stays (struck through) so a slip can be undone.
  const update = useMutation({
    mutationFn: ({ id, ...json }: { id: string; status: string }) => api(`${base}/tasks/${id}`, { method: "PATCH", json }),
    onMutate: async ({ id, status: next }) => {
      await qc.cancelQueries({ queryKey: listQuery.queryKey, exact: true });
      const before = qc.getQueryData<{ tasks: Task[] }>(listQuery.queryKey);
      qc.setQueryData<{ tasks: Task[] }>(listQuery.queryKey, (d) => d && { ...d, tasks: d.tasks.map((t) => (t.id === id ? { ...t, status: next as Task["status"] } : t)) });
      return { before };
    },
    onError: (_e, _v, ctx) => { if (ctx?.before) qc.setQueryData(listQuery.queryKey, ctx.before); },
    onSuccess: () => {
      const here = JSON.stringify(listQuery.queryKey);
      qc.invalidateQueries({ queryKey: [orgId, "tasks"], predicate: (q) => JSON.stringify(q.queryKey) !== here });
      qc.invalidateQueries({ queryKey: listQuery.queryKey, exact: true, refetchType: "none" });
    },
  });
  const now = Date.now();
  return (
    <>
      <PageHeader title="Tasks" />
      {!allows("readonly") && (
        <section className="card">
          <Form submitLabel="Add task" onSubmit={async (d) => {
            const due = str(d, "due");
            await api(`${base}/tasks`, { method: "POST", json: { title: str(d, "title"), priority: str(d, "priority"), ...(due && { due_at: new Date(due).toISOString() }) } });
            qc.invalidateQueries({ queryKey: [orgId, "tasks"] });
          }}>
            <div className="row">
              <Field label="Task"><input name="title" required maxLength={300} placeholder="e.g. Call Maryam about the quote" /></Field>
              <Field label="Due"><DateTimeInput name="due" /></Field>
              <Field label="Priority">
                <select name="priority" defaultValue="normal"><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option></select>
              </Field>
            </div>
          </Form>
        </section>
      )}
      <div className="filters">
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="open">Open</option><option value="done">Done</option><option value="all">All</option>
        </select>
        {allows("tasks_all") && (
          <select value={assigned} onChange={(e) => setAssigned(e.target.value)} aria-label="Assignee">
            <option value="me">Mine</option><option value="all">Everyone</option><option value="unassigned">Unassigned</option>
          </select>
        )}
      </div>
      <ErrorText error={update.error} />
      {tasks.isLoading ? <Loading /> : tasks.error ? <ErrorText error={tasks.error} /> : tasks.data!.tasks.length === 0 ? <Empty>{status === "open" ? (assigned === "me" ? "Nothing on your list. Nice." : "No open tasks.") : "Nothing here."}</Empty> : (
        <table className={`table${tasks.isPlaceholderData ? " is-stale" : ""}`}>
          <thead><tr><th className="pick"></th><th>Task</th><th>Contact</th><th>Due</th><th>Assignee</th><th>From</th></tr></thead>
          <tbody>
            {tasks.data!.tasks.map((t) => {
              const overdue = t.status === "open" && t.due_at && new Date(t.due_at).getTime() < now;
              return (
                <tr key={t.id} className={t.status !== "open" ? "done" : ""}>
                  <td className="pick">
                    <input type="checkbox" aria-label={`Mark ${t.title} done`} checked={t.status === "done"} disabled={allows("readonly")}
                      onChange={(e) => update.mutate({ id: t.id, status: e.target.checked ? "done" : "open" })} />
                  </td>
                  <td>
                    {t.priority === "high" && <span className="tag">High</span>} <span dir="auto">{t.title}</span>
                    {t.description && <div className="muted small">{t.description}</div>}
                  </td>
                  <td>{t.contact_id ? <Link to={`/o/${orgId}/contacts/${t.contact_id}`}>{t.contact_name ?? "Contact"}</Link> : t.thread_id ? <span className="muted">Not saved as a contact yet</span> : <span className="muted">—</span>}</td>
                  <td className={`nowrap small${overdue ? " overdue" : ""}`} title={t.due_at ? dateTime(t.due_at) : undefined}>{t.due_at ? `${overdue ? "Overdue · " : ""}${ago(t.due_at)}` : "—"}</td>
                  <td>{t.assignee_name ?? <span className="muted">Unassigned</span>}</td>
                  <td className="small">{t.thread_id ? <Link className="button-link secondary small" to={`/o/${orgId}/inbox/${t.thread_id}`}>Open conversation</Link> : fromLabel[t.created_by_type] ?? t.created_by_type}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}
